import { audio } from './audio';
import type { Dir, GameState, Phase } from './constants';
import { createGame, drainEvents, gridForViewport, moveProgress, queueDir, resetGame, step } from './sim';
import { drawWorld, layoutFor, type Layout } from './render';
import { loadBest, saveBest } from './save';

export interface HudSnapshot {
  score: number;
  best: number;
  phase: Phase;
  boostMs: number;
  isNewBest: boolean;
}

export interface EngineCallbacks {
  onHud: (hud: HudSnapshot) => void;
  onPlay: () => void;
  onGameOver: (durationMs: number) => void;
}

export class SnakeEngine {
  private ctx: CanvasRenderingContext2D;
  private cb: EngineCallbacks;
  private raf = 0;
  private last = 0;
  private running = false;
  private cssW = 800;
  private cssH = 600;
  private layout: Layout;
  private state: GameState;
  private best: number;
  private isNewBest = false;
  private runStartedAt = 0;
  private timeMs = 0;
  private playReported = false;

  constructor(ctx: CanvasRenderingContext2D, cb: EngineCallbacks) {
    this.ctx = ctx;
    this.cb = cb;
    this.best = loadBest();
    this.state = createGame({ cols: 17, rows: 17 });
    this.layout = layoutFor(800, 600, 17, 17);
  }

  get hud(): HudSnapshot {
    return {
      score: this.state.score,
      best: this.best,
      phase: this.state.phase,
      boostMs: this.state.boostMs,
      isNewBest: this.isNewBest,
    };
  }

  resize(width: number, height: number): void {
    this.cssW = width;
    this.cssH = height;
    if (this.state.phase === 'ready') {
      const grid = gridForViewport(width, height);
      if (grid.cols !== this.state.cols || grid.rows !== this.state.rows) {
        this.state = createGame({ cols: grid.cols, rows: grid.rows });
      }
    }
    this.layout = layoutFor(width, height, this.state.cols, this.state.rows);
    this.paint();
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(50, now - this.last);
      this.last = now;
      this.timeMs += dt;
      const phaseBefore = this.state.phase;
      step(this.state, dt);
      this.handleEvents(phaseBefore);
      this.paint();
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
    this.emitHud();
  }

  destroy(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  inputDir(dir: Dir): void {
    const wasReady = this.state.phase === 'ready';
    if (!queueDir(this.state, dir)) return;
    if (wasReady && this.state.phase === 'playing') {
      this.runStartedAt = performance.now();
      this.playReported = true;
      this.isNewBest = false;
      this.cb.onPlay();
      this.emitHud();
    }
  }

  replay(): void {
    if (this.state.phase !== 'gameover' && this.state.phase !== 'ready') return;
    this.state = resetGame(this.state);
    this.playReported = false;
    this.isNewBest = false;
    this.emitHud();
    this.paint();
  }

  private handleEvents(phaseBefore: Phase): void {
    const events = drainEvents(this.state);
    for (const ev of events) {
      if (ev.type === 'eat') audio.play(ev.kind === 'golden' ? 'golden' : 'eat');
      else if (ev.type === 'boost') audio.play('boost');
      else if (ev.type === 'portal') audio.play('portal');
      else if (ev.type === 'die') audio.play('die');
    }
    if (phaseBefore !== 'gameover' && this.state.phase === 'gameover') {
      const nextBest = saveBest(this.state.score);
      this.isNewBest = this.state.score > 0 && this.state.score >= nextBest && this.state.score > this.best;
      this.best = nextBest;
      if (this.playReported) {
        this.cb.onGameOver(performance.now() - this.runStartedAt);
      }
      this.emitHud();
    } else if (events.length) {
      this.emitHud();
    } else if (this.state.boostMs > 0) {
      this.emitHud();
    }
  }

  private emitHud(): void {
    this.cb.onHud(this.hud);
  }

  private paint(): void {
    drawWorld(this.ctx, this.state, this.layout, moveProgress(this.state), this.timeMs);
  }
}
